import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FormSelect, NotificationHub } from "@nef/core"
import { Controller, useWatch, useForm } from "react-hook-form"

import { fetchCountriesList } from "../../redux/utilities/actions"
import { AppDispatch, RootState } from "../../app/store"
import { Country } from "../../types/common"
import { AdminRegion } from "../../generated/api"
import CountriesResponse from "../../../jest/Registration/responses/CountriesResponse"

interface Props {
  selectedCountry: Country | null
  setSelectedCountry: Function
  selectedRegion: AdminRegion | null
  setSelectedRegion: Function
  isRegionOptional?: boolean
  isCountryOptional?: boolean
  isCurrencyOptional?: boolean
  supportHookForm: boolean
  control?: any
  countryLabel?: string
  regionLabel?: string
  currencyLabel?: string
  showCurrencySelector?: boolean
  showRegionSelector?: boolean
  setSelectedCurrency?: Function
  selectedCurrency?: any
}

const SearchableCountryRegionSelector = ({
  selectedCountry,
  setSelectedCountry,
  selectedRegion,
  setSelectedRegion,
  isRegionOptional = true,
  isCountryOptional = false,
  isCurrencyOptional = true,
  supportHookForm = false,
  control,
  countryLabel,
  regionLabel,
  currencyLabel,
  showCurrencySelector = false,
  showRegionSelector = true,
  setSelectedCurrency,
  selectedCurrency
}: Props) => {
  const dispatch = useDispatch<AppDispatch>()

  const countriesList: any = useSelector((state: RootState) => state.utilities.countriesList)
  const [regions, setRegions] = useState<AdminRegion[]>([])
  const [currencies, setCurrencies] = useState<any[]>([])

  // used to handle non-hook form rendering
  const { control: newControl } = useForm()
  const countryWatch = useWatch({ name: "country", control: control || newControl })

  const getCountries = async () => {
    try {
      dispatch(fetchCountriesList()).unwrap()
    } catch (error: any) {
      NotificationHub.send("danger", `${error.code} : ${error.message}`)
    }
  }

  useEffect(() => {
    if (!countriesList?.length) {
      getCountries()
      return
    }
    setCurrencies([...new Set(countriesList.map((country: Country) => country.currencyEnglishName))])
  }, [countriesList])

  useEffect(() => {
    if (!countryWatch) return
    let country: Country | undefined = countriesList?.find((_country: Country) => _country.nameEnglish === countryWatch.name)
    if (!country) country = countriesList?.find((_country: Country) => _country.nameEnglish === countryWatch)
    if (country) setRegions(country?.regions || [])
  }, [countryWatch])

  return (
    <>
      {countriesList?.length && (
        <>
          {!supportHookForm ? (
            <div className="countries__container">
              <FormSelect
                label={countryLabel || "Country"}
                name="countries"
                placeholder="Select Country"
                isClearable={false} // change this to get a clear behavior
                options={countriesList?.map((country: any) => ({
                  label: country.nameEnglish,
                  value: country.code3CountryLetterIso3166
                }))}
                onChange={(e: any) => {
                  const country: Country = countriesList.find((_country: Country) => _country.code3CountryLetterIso3166 === e.value.value) || {}
                  setSelectedCountry(country)
                }}
                value={{
                  label: selectedCountry?.nameEnglish,
                  value: selectedCountry?.code3CountryLetterIso3166
                }}
                optional={isCountryOptional}
                className="countries__country-selector"
              />

              {selectedCountry?.regions?.length && showRegionSelector ? (
                <FormSelect
                  label={regionLabel || "Region"}
                  name="regions"
                  placeholder={`Select ${regionLabel || 'Region'}`}
                  isClearable={false} // change this to get a clear behavior
                  options={selectedCountry?.regions?.map((region: AdminRegion) => ({
                    label: region.name,
                    value: region.code
                  }))}
                  onChange={(e: any) => {
                    const region = selectedCountry?.regions?.find((_region: AdminRegion) => _region.code === e.value.value)
                    setSelectedRegion(region)
                  }}
                  value={{
                    label: selectedRegion?.name,
                    value: selectedRegion?.code
                  }}
                  optional={isRegionOptional}
                  className="countries__region-selector"
                />
              ) : (
                ""
              )}
              {showCurrencySelector && selectedCurrency && setSelectedCurrency && (
                <FormSelect
                  label={currencyLabel || "Currency"}
                  name="currencies"
                  placeholder="Select Currency"
                  isClearable={false} // change this to get a clear behavior
                  options={currencies.map((currency: any) => ({
                    label: currency,
                    value: countriesList.find((country: Country) => country.currencyEnglishName === currency).code3CurrencyISO4217
                  }))}
                  onChange={(e: any) => {
                    setSelectedCurrency(e.value)
                  }}
                  value={selectedCurrency}
                  optional={isCurrencyOptional}
                  className="countries__currency-selector"
                />
              )}
            </div>
          ) : (
            <div className="countries__container">
              <Controller
                name="country"
                control={control}
                rules={{ required: { value: !isCountryOptional, message: "Country is required" } }}
                render={({ field: { onChange, value, name }, fieldState, formState }) => (
                  <FormSelect
                    label="Country"
                    name={name}
                    value={{ label: value?.name, value: value?.code }}
                    placeholder="Select Country"
                    isClearable={false} // change this to get a clear behavior
                    options={countriesList?.map((country: any) => ({
                      label: country.nameEnglish,
                      value: country.code3CountryLetterIso3166
                    }))}
                    onChange={(e: any) => {
                      const country: Country = countriesList.find((_country: Country) => _country.code3CountryLetterIso3166 === e.value.value) || {}
                      setSelectedCountry(country)
                      onChange({ name: country.nameEnglish, code: country.code3CountryLetterIso3166 })
                    }}
                    optional={isCountryOptional}
                    className="countries__country-selector"
                    invalid={fieldState.error != null}
                    feedback={fieldState.error?.message}
                  />
                )}
              />
              {regions?.length ? (
                <Controller
                  name="region"
                  rules={{ required: { value: !isRegionOptional ? regions.length > 0 : !isRegionOptional, message: "Region is required" } }}
                  control={control}
                  render={({ field: { onChange, value, name }, fieldState, formState }) => (
                    <FormSelect
                      label="Region"
                      value={{ label: value?.name, value: value?.code }}
                      name={name}
                      placeholder="Select Region"
                      isClearable={false} // change this to get a clear behavior
                      options={regions?.map((region: AdminRegion) => ({
                        label: region.name,
                        value: region.code
                      }))}
                      onChange={(e: any) => {
                        const region = regions?.find((_region: AdminRegion) => _region.code === e.value.value)
                        setSelectedRegion(region)
                        onChange({ name: region?.name, code: region?.code })
                      }}
                      optional={isRegionOptional}
                      className="countries__region-selector"
                      invalid={fieldState.error != null}
                      feedback={fieldState.error?.message}
                    />
                  )}
                />
              ) : (
                ""
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}

export default SearchableCountryRegionSelector
